import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Clock, CheckCircle2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import Logo from "@/components/Logo";
import VirtualAbacus from "@/components/VirtualAbacus";

function fmtElapsed(sec) {
  const m = Math.floor(sec / 60).toString().padStart(2, "0");
  const s = Math.floor(sec % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}

function GridCard({ q, idx, type, value, onChange }) {
  return (
    <div data-testid={`grid-q-${idx}`} className="p-4 bg-white rounded-2xl border-2 border-cloud">
      <div className="flex items-center justify-between mb-2">
        <span className="w-7 h-7 rounded-full bg-ink text-white text-xs font-bold inline-flex items-center justify-center">{idx + 1}</span>
        {value !== "" && value != null && <CheckCircle2 className="w-4 h-4 text-leaf" />}
      </div>

      {type === "arithmetic" && (
        <div className="font-heading text-2xl font-bold text-ink text-right tabular-nums mb-3 leading-snug">
          <div>{q.starting_number}</div>
          {q.operations.map((op, i) => (
            <div key={i}>
              <span className={op.op === "+" ? "text-leaf" : op.op === "-" ? "text-watermelon" : op.op === "×" ? "text-sky" : "text-sky-hover"}>{op.op}</span>{op.value}
            </div>
          ))}
          <div className="border-t-2 border-ink mt-1 pt-1">?</div>
        </div>
      )}

      {type === "read" && (
        <div className="mb-2">
          <div className="text-[10px] font-bold uppercase tracking-wider text-pebble mb-1">Show on abacus</div>
          <div className="font-heading text-3xl font-bold text-ink text-center tabular-nums mb-2">{q.starting_number}</div>
          <VirtualAbacus
            rods={4}
            onesRodIndex={3}
            initialValue={value ? Number(value) : 0}
            onValueChange={(v) => onChange(String(v))}
          />
          <div className="mt-1 text-center text-xs font-bold text-stone">Shows: <span className="text-ink">{value || 0}</span></div>
        </div>
      )}

      {type === "represent" && (
        <div className="mb-2">
          <div className="text-[10px] font-bold uppercase tracking-wider text-pebble mb-1">Read the beads</div>
          <VirtualAbacus rods={4} onesRodIndex={3} readOnly initialValue={q.starting_number || 0} />
        </div>
      )}

      {type !== "read" && (
        <input
          data-testid={`grid-answer-${idx}`}
          inputMode="numeric"
          pattern="-?[0-9]*"
          value={value}
          onChange={(e) => onChange(e.target.value.replace(/[^0-9-]/g, ""))}
          placeholder="?"
          className="w-full h-12 text-center text-2xl font-heading font-bold text-ink rounded-xl border-2 border-cloud focus:border-sky focus:outline-none tabular-nums bg-canvas"
        />
      )}
    </div>
  );
}

export default function WorksheetGrid({ attemptId, startedAt, exercise, questions, initialAnswers }) {
  const navigate = useNavigate();
  const [answers, setAnswers] = useState(() => {
    const map = {};
    questions.forEach((q) => {
      const v = initialAnswers?.[q.id];
      map[q.id] = v == null ? "" : String(v);
    });
    return map;
  });
  const [elapsed, setElapsed] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const type = exercise?.type || "arithmetic";
  const total = questions.length;
  const answeredCount = useMemo(
    () => Object.values(answers).filter((v) => v !== "" && v != null).length,
    [answers]
  );

  useEffect(() => {
    if (!startedAt) return;
    const start = new Date(startedAt).getTime();
    const tick = () => setElapsed(Math.max(0, Math.floor((Date.now() - start) / 1000)));
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [startedAt]);

  const setAnswer = (qid, val) => setAnswers((prev) => ({ ...prev, [qid]: val }));

  const submit = async () => {
    if (answeredCount === 0) {
      toast.error("Answer at least one question before submitting.");
      return;
    }
    if (answeredCount < total && !window.confirm(`${total - answeredCount} question(s) are unanswered. Submit anyway?`)) {
      return;
    }
    setSubmitting(true);
    try {
      for (const q of questions) {
        const v = answers[q.id];
        if (v === "" || v == null) continue;
        const parsed = parseInt(v, 10);
        if (Number.isNaN(parsed)) continue;
        await api.post(`/student/attempts/${attemptId}/answer`, { question_id: q.id, answer: parsed });
      }
      await api.post(`/student/attempts/${attemptId}/submit`);
      toast.success("Worksheet submitted!");
      navigate(`/result/${attemptId}`, { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-canvas flex flex-col">
      <header className="sticky top-0 z-30 border-b border-cloud bg-white/85 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
          <Logo />
          <div className="flex items-center gap-3">
            <div data-testid="timer" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-sky-light border-2 border-sky/20 font-mono font-bold text-sky-hover tabular-nums">
              <Clock className="w-4 h-4" /> {fmtElapsed(elapsed)}
            </div>
            <button data-testid="exit-practice-button" onClick={() => navigate("/student")} className="text-sm font-bold text-stone hover:text-ink px-3 py-2 rounded-full">Exit</button>
          </div>
        </div>
      </header>

      <main className="flex-1 pb-28">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
          <div className="mb-6">
            <div className="text-xs font-bold uppercase tracking-wider text-stone">{exercise.title}</div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-ink">Worksheet — answer all {total} questions</h1>
            <p className="text-sm text-stone mt-1">Type your answer below each question, just like your practice book. Submit when done.</p>
            <div className="mt-3 h-3 bg-white rounded-full overflow-hidden border border-cloud">
              <div className="h-full bg-gradient-to-r from-watermelon to-lemon transition-all" style={{ width: `${(answeredCount / Math.max(1, total)) * 100}%` }} />
            </div>
            <div data-testid="grid-progress" className="mt-1 text-xs font-bold text-stone">{answeredCount} of {total} answered</div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {questions.map((q, idx) => (
              <GridCard
                key={q.id}
                q={q}
                idx={idx}
                type={type}
                value={answers[q.id] ?? ""}
                onChange={(v) => setAnswer(q.id, v)}
              />
            ))}
          </div>
        </div>
      </main>

      <div className="fixed bottom-0 inset-x-0 z-30 border-t border-cloud bg-white/90 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between gap-4">
          <div className="text-sm font-bold text-stone">{answeredCount} / {total} answered</div>
          <button
            data-testid="grid-submit-button"
            onClick={submit}
            disabled={submitting}
            className="inline-flex items-center gap-2 h-12 px-8 rounded-full bg-watermelon text-white font-heading font-bold text-lg btn-tactile hover:bg-watermelon-hover disabled:opacity-70"
          >
            <CheckCircle2 className="w-5 h-5" /> {submitting ? "Submitting…" : "Submit worksheet"}
          </button>
        </div>
      </div>
    </div>
  );
}
